import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Pencil, Trash2, Plus, Copy, GripVertical } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import { api, fileSrc, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

export const field = "w-full rounded-xl border border-white/10 bg-ink px-4 py-3 text-paper outline-none transition-colors focus:border-volt";
const sw = "data-[state=checked]:bg-volt data-[state=unchecked]:!bg-zinc-700";
const btn = "rounded-full bg-volt px-5 py-2.5 text-sm font-semibold text-ink transition-transform hover:scale-[1.03] disabled:opacity-60";
const ghost = "rounded-full border border-white/15 px-5 py-2.5 text-sm text-zinc-200 hover:border-volt";
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

export const ImageUpload = ({ url, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.path, data.url);
    } catch (x) {
      err(x);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-4">
      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-ink ring-1 ring-white/10">{url ? <img src={fileSrc(url)} alt="" className="h-full w-full object-cover" /> : null}</div>
      <label className={`${ghost} flex cursor-pointer items-center gap-2`}>
        <Upload className="h-4 w-4" /> {busy ? "Mengupload…" : "Upload gambar"}
        <input data-testid={testId} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={pick} className="hidden" />
      </label>
    </div>
  );
};

export const EventSettings = () => {
  const [f, setF] = useState(null);
  const [zonkUrl, setZonkUrl] = useState("");
  useEffect(() => {
    api.get("/admin/gacha/settings").then((r) => { setF(r.data); setZonkUrl(r.data?.zonk_image ? `/api/files/${r.data.zonk_image}` : ""); }).catch(() => setF(null));
  }, []);
  if (!f) return null;
  const save = () => api.put("/admin/gacha/settings", f).then(() => toast.success("Pengaturan event disimpan")).catch(err);
  const toggle = (k, v) => {
    const next = { ...f, [k]: v };
    setF(next);
    api.put("/admin/gacha/settings", next).then(() => toast.success(v ? (k === "enabled" ? "Event dinyalakan" : "Mode uji coba dinyalakan") : (k === "enabled" ? "Event dimatikan" : "Mode uji coba dimatikan"))).catch((e) => { setF(f); err(e); });
  };
  return (
    <div className="space-y-4" data-testid="gacha-admin-settings">
      <label className="flex items-center gap-3 text-paper">
        <Switch data-testid="gacha-admin-enabled" checked={f.enabled} onCheckedChange={(v) => toggle("enabled", v)} className={sw} />
        {f.enabled ? "Event AKTIF — halaman /brewek tampil di menu" : "Event mati — halaman /brewek tersembunyi"}
      </label>
      <label className="flex items-center gap-3 text-paper">
        <Switch data-testid="gacha-admin-test-mode" checked={!!f.test_mode} onCheckedChange={(v) => toggle("test_mode", v)} className={sw} />
        {f.test_mode ? "Mode uji coba AKTIF — hanya admin yang sedang login di browser ini bisa membuka & mencoba /brewek (kode apa saja, voucher tidak terpakai). Pengunjung lain tidak melihatnya." : "Mode uji coba mati"}
      </label>
      <input data-testid="gacha-admin-title" placeholder="Judul event" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} />
      <textarea data-testid="gacha-admin-subtitle" rows={2} placeholder="Deskripsi singkat event (opsional)" value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} className={field} />
      <p className="pt-2 text-sm text-zinc-400">Kartu zonk (kurang beruntung)</p>
      <input data-testid="gacha-admin-zonk-name" value={f.zonk_name} onChange={(e) => setF({ ...f, zonk_name: e.target.value })} className={field} />
      <ImageUpload testId="gacha-admin-zonk-image" url={zonkUrl} onChange={(path, url) => { setF({ ...f, zonk_image: path }); setZonkUrl(url); }} />
      <button data-testid="gacha-admin-settings-save" onClick={save} className={btn}>Simpan event</button>
    </div>
  );
};

const EMPTY = { name: "", image: "", image_url: "", rate: "", kind: "code", order: 0, active: true };

const lines = (t) => t.split("\n").map((v) => v.trim()).filter(Boolean);
const isUrl = (v) => /^https?:\/\//i.test(v);
const voucherError = (kind, vals) => {
  if (kind === "link" && vals.some((v) => !isUrl(v))) return "Jenis Link voucher: setiap baris harus berupa link yang diawali https://";
  if (kind === "code" && vals.some(isUrl)) return "Jenis Kode voucher: masukkan kode, bukan link";
  return "";
};
const KINDS = { code: "Kode voucher", link: "Link voucher" };

const VoucherInput = ({ kind, value, onChange, testId }) => {
  const msg = voucherError(kind, lines(value));
  return (
    <div className="space-y-1">
      <label className="text-sm text-zinc-300">{kind === "link" ? "Link voucher (satu link per baris)" : "Kode voucher (satu kode per baris)"}</label>
      <textarea data-testid={testId} rows={4} value={value} onChange={(e) => onChange(e.target.value)} placeholder={kind === "link" ? "https://contoh.com/voucher-1\nhttps://contoh.com/voucher-2" : "OVO50-ABC123\nOVO50-XYZ789"} className={`${field} font-mono text-sm`} />
      <p data-testid={`${testId}-info`} className={`text-xs ${msg ? "text-red-400" : "text-zinc-400"}`}>{msg || `${lines(value).length} ${KINDS[kind].toLowerCase()} akan ditambahkan ke stok`}</p>
    </div>
  );
};

const PrizeForm = ({ initial, onDone, onCancel }) => {
  const [f, setF] = useState(initial);
  const [vouchers, setVouchers] = useState("");
  const kindLocked = Boolean(f.id && f.stock > 0);
  const save = async (e) => {
    e.preventDefault();
    const vals = lines(vouchers);
    const msg = voucherError(f.kind, vals);
    if (msg) return toast.error(msg);
    const body = { name: f.name, image: f.image, rate: Number(f.rate), kind: f.kind, active: f.active };
    try {
      let id = f.id;
      if (id) await api.put(`/admin/gacha/prizes/${id}`, body);
      else id = (await api.post("/admin/gacha/prizes", body)).data?.id;
      if (vals.length && id) await api.post(`/admin/gacha/prizes/${id}/vouchers`, { values: vals });
      toast.success(vals.length ? `Hadiah disimpan + ${vals.length} voucher masuk stok` : "Hadiah disimpan");
      onDone();
    } catch (x) {
      err(x);
    }
  };
  return (
    <form onSubmit={save} data-testid="gacha-prize-form" className="space-y-4 rounded-2xl border border-volt/30 p-5">
      <input data-testid="gacha-prize-name" required placeholder="Nama hadiah (misal: Voucher OVO 50rb)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} />
      <ImageUpload testId="gacha-prize-image" url={f.image_url} onChange={(path, url) => setF({ ...f, image: path, image_url: url })} />
      <label className="block text-sm text-zinc-300">Rate (%)<input data-testid="gacha-prize-rate" required type="number" step="0.0001" min="0.0001" max="100" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} className={`mt-1 ${field}`} /></label>
      <div className="flex flex-wrap gap-2 text-sm">
        {[["code", "Kode voucher (ditampilkan langsung)"], ["link", "Link voucher (tombol klik di sini)"]].map(([k, l]) => (
          <button type="button" key={k} disabled={kindLocked && f.kind !== k} data-testid={`gacha-prize-kind-${k}`} onClick={() => { if (f.kind !== k) { setF({ ...f, kind: k }); setVouchers(""); } }} className={`rounded-full border px-4 py-2 disabled:cursor-not-allowed disabled:opacity-40 ${f.kind === k ? "border-volt bg-volt text-ink" : "border-white/15 text-zinc-300"}`}>{l}</button>
        ))}
      </div>
      {kindLocked ? <p className="text-xs text-zinc-400">Jenis tidak bisa diganti karena masih ada {f.stock} stok {KINDS[f.kind].toLowerCase()}.</p> : null}
      <VoucherInput kind={f.kind} value={vouchers} onChange={setVouchers} testId="gacha-prize-vouchers" />
      <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} className={sw} /> {f.active ? "Ikut diundi" : "Tidak diundi"}</label>
      <div className="flex gap-3"><button data-testid="gacha-prize-save" className={btn}>Simpan</button><button type="button" onClick={onCancel} className={ghost}>Batal</button></div>
    </form>
  );
};

const VoucherAdder = ({ prize, onDone }) => {
  const [text, setText] = useState("");
  const [list, setList] = useState(null);
  const add = async () => {
    const msg = voucherError(prize.kind, lines(text));
    if (msg) return toast.error(msg);
    try {
      const { data } = await api.post(`/admin/gacha/prizes/${prize.id}/vouchers`, { values: lines(text) });
      toast.success(`${data.added} voucher ditambahkan`);
      setText("");
      onDone();
    } catch (x) {
      err(x);
    }
  };
  const show = () => api.get(`/admin/gacha/prizes/${prize.id}/vouchers`).then((r) => setList(Array.isArray(r.data) ? r.data : []));
  return (
    <div className="mt-3 space-y-2 rounded-xl bg-ink p-4" data-testid={`gacha-vouchers-${prize.id}`}>
      <VoucherInput kind={prize.kind} value={text} onChange={setText} testId={`gacha-voucher-input-${prize.id}`} />
      <div className="flex flex-wrap gap-2">
        <button data-testid={`gacha-voucher-add-${prize.id}`} disabled={!text.trim()} onClick={add} className={btn}>Tambah ke stok</button>
        <button onClick={show} className={ghost}>Lihat voucher</button>
      </div>
      {list ? list.map((v, i) => <p key={i} className="truncate font-mono text-xs text-zinc-400">{v.used ? "✔ dipakai" : "○ tersedia"} · {v.value}{v.code ? ` · ${v.code}` : ""}</p>) : null}
    </div>
  );
};

const PrizeItem = ({ p, open, setOpen, onEdit, onRemove, onDrop, load }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={p} dragListener={false} dragControls={controls} onDragEnd={onDrop} className="relative border-b border-white/[0.07] bg-surface py-4" data-testid={`gacha-admin-prize-${p.id}`}>
      <div className="flex items-center gap-3 sm:gap-4">
        <button type="button" onPointerDown={(e) => controls.start(e)} style={{ touchAction: "none" }} data-testid={`gacha-prize-drag-${p.id}`} aria-label="Geser urutan" className="grid h-9 w-7 shrink-0 cursor-grab place-items-center text-zinc-400 active:cursor-grabbing"><GripVertical className="h-5 w-5" /></button>
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-ink">{p.image_url ? <img src={fileSrc(p.image_url)} alt="" draggable={false} className="h-full w-full object-cover" /> : null}</div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-paper">{p.name} {!p.active ? <span className="text-xs text-zinc-500">(tidak diundi)</span> : null}</p>
          <p className="text-sm text-zinc-400">Rate {p.rate}% · {p.kind === "link" ? "Link" : "Kode"} · Stok {p.stock} dari {p.total}</p>
          {p.stock === 0 ? <p data-testid={`gacha-prize-empty-${p.id}`} className="text-xs font-medium text-red-400">Stok kosong, pemain akan dapat zonk. Klik "Isi voucher".</p> : null}
        </div>
        <button onClick={() => setOpen(open === p.id ? null : p.id)} data-testid={`gacha-prize-stock-${p.id}`} className={`${ghost} whitespace-nowrap`}>Isi voucher</button>
        <button onClick={onEdit} data-testid={`gacha-prize-edit-${p.id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
        <button onClick={onRemove} data-testid={`gacha-prize-delete-${p.id}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 hover:border-red-400" aria-label="Hapus"><Trash2 className="h-4 w-4" /></button>
      </div>
      {open === p.id ? <VoucherAdder prize={p} onDone={load} /> : null}
    </Reorder.Item>
  );
};

export const PrizesAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/gacha/prizes").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);
  const total = items.filter((p) => p.active).reduce((s, p) => s + p.rate, 0);
  const remove = async (p) => {
    if (!window.confirm(`Hapus hadiah "${p.name}" beserta voucher yang belum terpakai?`)) return;
    await api.delete(`/admin/gacha/prizes/${p.id}`).catch(err);
    load();
  };
  const saveOrder = () => {
    api.put("/admin/gacha/prizes-order", { ids: itemsRef.current.map((p) => p.id) }).then(() => toast.success("Urutan tampil disimpan")).catch(err);
  };
  const itemsRef = useRef(items);
  itemsRef.current = items;
  return (
    <div className="space-y-4" data-testid="gacha-admin-prizes">
      <p data-testid="gacha-rate-summary" className="text-sm text-zinc-300">Total rate hadiah <span className="text-volt">{+total.toFixed(4)}%</span> · Zonk <span className="text-volt">{+(100 - total).toFixed(4)}%</span>. Hadiah yang stoknya habis otomatis jadi zonk.</p>
      {editing ? <PrizeForm initial={editing} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} /> : <button data-testid="gacha-prize-new" onClick={() => setEditing(EMPTY)} className={`${btn} flex items-center gap-2`}><Plus className="h-4 w-4" /> Hadiah baru</button>}
      {items.length > 1 ? <p className="text-xs text-zinc-400">Tahan & geser ikon <GripVertical className="inline h-3.5 w-3.5" /> untuk mengatur urutan tampil di halaman Brewek.</p> : null}
      <Reorder.Group axis="y" values={items} onReorder={setItems} className="space-y-0" data-testid="gacha-prize-list-admin">
        {items.map((p) => (
          <PrizeItem key={p.id} p={p} open={open} setOpen={setOpen} onEdit={() => setEditing(p)} onRemove={() => remove(p)} onDrop={saveOrder} load={load} />
        ))}
      </Reorder.Group>
    </div>
  );
};

export const CodesAdmin = () => {
  const [count, setCount] = useState(10);
  const [note, setNote] = useState("");
  const [codes, setCodes] = useState([]);
  const load = () => api.get("/admin/gacha/codes").then((r) => setCodes(Array.isArray(r.data) ? r.data : [])).catch(() => setCodes([]));
  useEffect(() => {
    load();
  }, []);
  const gen = () => api.post("/admin/gacha/codes", { count: Number(count), note }).then((r) => { toast.success(`${r.data.codes.length} kode dibuat`); load(); }).catch(err);
  const unused = codes.filter((c) => !c.used);
  const delOne = (c) => api.delete(`/admin/gacha/codes/${encodeURIComponent(c)}`).then(() => { toast.success(`Kode ${c} dihapus`); load(); }).catch(err);
  const delAll = () => {
    if (!window.confirm(`Hapus ${unused.length} kode yang belum dipakai? Kode yang sudah dipakai tetap tersimpan sebagai riwayat.`)) return;
    api.delete("/admin/gacha/codes").then((r) => { toast.success(`${r.data?.deleted ?? 0} kode dihapus`); load(); }).catch(err);
  };
  const copy = () => navigator.clipboard?.writeText(unused.map((c) => c.code).join("\n")).then(() => toast.success(`${unused.length} kode disalin`));
  return (
    <div className="space-y-4" data-testid="gacha-admin-codes">
      <div className="flex flex-wrap gap-2">
        <input data-testid="gacha-codes-count" type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)} className={`${field} w-28`} />
        <input data-testid="gacha-codes-note" placeholder="Catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} className={`${field} min-w-0 flex-1`} />
        <button data-testid="gacha-codes-generate" onClick={gen} className={btn}>Buat kode</button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-zinc-400">
        <span>{unused.length} belum dipakai · {codes.length - unused.length} sudah dipakai</span>
        <div className="flex flex-wrap gap-2">
          <button data-testid="gacha-codes-copy" disabled={!unused.length} onClick={copy} className={`${ghost} flex items-center gap-2`}><Copy className="h-4 w-4" /> Salin semua yang belum dipakai</button>
          <button data-testid="gacha-codes-delete-unused" disabled={!unused.length} onClick={delAll} className={`${ghost} flex items-center gap-2 hover:border-red-400 hover:text-red-300 disabled:opacity-40`}><Trash2 className="h-4 w-4" /> Hapus semua yang belum dipakai</button>
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto rounded-xl bg-ink p-3 font-mono text-xs">
        {codes.map((c) => (
          <p key={c.code} className="flex justify-between gap-3 py-1 text-zinc-300"><span>{c.code}</span><span className="flex items-center gap-3"><span className={c.used ? "text-zinc-400" : "text-lime"}>{c.used ? `dipakai → ${c.result || "-"}` : "tersedia"}</span>{!c.used ? <button data-testid={`gacha-code-delete-${c.code}`} onClick={() => delOne(c.code)} aria-label={`Hapus ${c.code}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button> : null}</span></p>
        ))}
      </div>
    </div>
  );
};

export const PullsLog = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/gacha/pulls").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, []);
  if (!rows.length) return <p className="text-sm text-zinc-400" data-testid="gacha-pulls-empty">Belum ada yang brewek.</p>;
  return (
    <div className="max-h-96 overflow-auto" data-testid="gacha-pulls-log">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="text-xs uppercase tracking-wider text-zinc-400"><tr><th className="py-2">Waktu</th><th>Kode</th><th>Hasil</th><th>Voucher</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/[0.07] text-zinc-300">
              <td className="py-2 pr-3">{new Date(r.created_at).toLocaleString("id-ID")}</td>
              <td className="pr-3 font-mono">{r.code}</td>
              <td className={`pr-3 ${r.zonk ? "text-zinc-500" : "text-volt"}`}>{r.name}</td>
              <td className="max-w-[220px] truncate font-mono text-xs">{r.voucher || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
